import React from 'react';
import { identifySiteVisitor, getSiteVisitorId } from './siteTraffic';

type Props = {
  language?: 'en' | 'es';
  /** High-intent path copy (pricing, demo, CMMC guide). */
  offer?: 'linkedin_deal' | 'checklist' | 'soft_email';
  companyHint?: string;
};

/**
 * First-party assent patterns (not covert unmasking):
 * - LinkedIn Sign-In deal
 * - Soft email capture with clear value exchange
 * - Progressive “one more field” after known visitor
 */
const AssentCapture: React.FC<Props> = ({
  language = 'en',
  offer = 'linkedin_deal',
  companyHint = '',
}) => {
  const spanish = language === 'es';
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState('');
  const [done, setDone] = React.useState(false);
  const known = Boolean(getSiteVisitorId());

  const submitSoft = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    identifySiteVisitor({
      email: trimmed,
      company_name: companyHint,
      source: offer === 'checklist' ? 'content_gate' : 'soft_email',
      path: typeof window !== 'undefined' ? window.location.pathname : '/',
    });
    if (role.trim()) {
      identifySiteVisitor({
        email: trimmed,
        full_name: role.trim(),
        company_name: companyHint,
        source: 'progressive_profile',
      });
    }
    setDone(true);
  };

  if (done) {
    return (
      <aside className="assent-capture is-done" role="status">
        <p>
          {spanish
            ? 'Gracias — ya podemos asociar esta visita a tu correo (tú lo diste).'
            : 'Thanks — we can tie this visit to your email (you gave it).'}
        </p>
      </aside>
    );
  }

  if (offer === 'linkedin_deal') {
    return (
      <aside className="assent-capture" aria-label={spanish ? 'Identifícate' : 'Identify yourself'}>
        <p className="assent-capture__lead">
          {spanish
            ? '¿Prefieres no rellenar formularios? Entra con LinkedIn y obtén 20% off los primeros 3 meses.'
            : 'Hate forms? Sign in with LinkedIn and get 20% off your first 3 months.'}
        </p>
        <a className="btn btn-primary" href="/website/signup">
          {spanish ? 'Continuar con LinkedIn' : 'Continue with LinkedIn'}
        </a>
        <p className="assent-capture__fine">
          {spanish
            ? 'Solo usamos el nombre y correo que LinkedIn comparte contigo — no rastreamos perfiles anónimos.'
            : 'We only use the name and email LinkedIn shares with you — we do not unmask anonymous browsers.'}
        </p>
      </aside>
    );
  }

  return (
    <aside className="assent-capture" aria-label={spanish ? 'Captura con valor' : 'Value exchange capture'}>
      <p className="assent-capture__lead">
        {offer === 'checklist'
          ? (spanish
            ? 'Deja tu correo de trabajo y te enviamos la lista de preparación bid-ready.'
            : 'Leave your work email and we will send the bid-ready prep checklist.')
          : (spanish
            ? 'Un correo de trabajo — te avisamos cuando publiquemos la guía.'
            : 'One work email — we will ping you when the guide drops.')}
        {companyHint ? ` (${companyHint})` : ''}
      </p>
      <form className="assent-capture__form" onSubmit={submitSoft}>
        <input
          type="email"
          required
          placeholder={spanish ? 'tú@empresa.com' : 'you@company.com'}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
        />
        {known ? (
          <input
            type="text"
            placeholder={spanish ? 'Tu rol (opcional)' : 'Your role (optional)'}
            value={role}
            onChange={(event) => setRole(event.target.value)}
            autoComplete="organization-title"
          />
        ) : null}
        <button className="btn btn-primary" type="submit">
          {spanish ? 'Enviar' : 'Send it'}
        </button>
      </form>
    </aside>
  );
};

export default AssentCapture;
